import { isFuture } from "date-fns";
import { values } from "lodash";
import { createSelector } from "reselect";

import { Right } from "constants/right";
import { SYSTEM_USER_ID } from "constants/user";
import { ILivechatGroup } from "interfaces/entities/livechat";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { IEntityCollectionState } from "store/entities/reducer";
import { IWithSessionState, hasRight } from "store/features/session";
import { IWithRequestsState, createRequestFetchingSelector } from "store/requests/selectors";

import { IWithAgentsSessionState, IWithAgentsState, selectAgents } from "../agents";
import { DataStatus, IWithLicensesState, selectLicenseCreatorId } from "../license";
import { selectCurrentSubscription } from "../subscriptions";
import { IWithTeamsState, selectTeams } from "../teams";

import { IIntegration, ILiveChatImportDiff, ILiveChatSelectiveImport, IntegrationType } from "./interfaces";

export interface IWithIntegrationsState {
  entities: {
    integrations: IEntityCollectionState<IIntegration>;
  };
}

function selectIntegrations(state: IWithIntegrationsState): IEntityCollectionState<IIntegration> {
  return state.entities.integrations;
}

const selectActiveIntegrations = createSelector(selectIntegrations, integrations =>
  Object.values(integrations).filter(integration => integration.active)
);

export const selectLiveChatIntegration = createSelector(selectActiveIntegrations, integrations =>
  integrations.find(integration => integration.type === IntegrationType.LiveChat)
);

export const selectLiveChatSelectiveImportDiff = (state: IWithIntegrationsState): ILiveChatImportDiff | undefined => {
  const liveChatIntegration = selectLiveChatIntegration(state);
  return liveChatIntegration && liveChatIntegration.importDiff;
};

export const selectIsFetchingLiveChatSelectiveImportDiff = (state: IWithRequestsState): boolean => {
  return createRequestFetchingSelector(["ENTITIES/INTEGRATION/LIVECHAT_IMPORT_DIFF_FETCH"])(state);
};

export const selectLiveChatSelectiveImport = (state: IWithIntegrationsState): ILiveChatSelectiveImport | undefined => {
  const liveChatIntegration = selectLiveChatIntegration(state);
  return liveChatIntegration && liveChatIntegration.selectiveImport;
};

export const selectLiveChatAllSelectiveImportsFinished = (state: IWithIntegrationsState): boolean => {
  const selectiveImport = selectLiveChatSelectiveImport(state);
  return selectiveImport ? values(selectiveImport).every(item => item.status === "complete") : false;
};

export const selectLiveChatSelectiveImportInProgress = createSelector(
  selectLiveChatSelectiveImportDiff,

  selectiveImportDiff =>
    selectiveImportDiff ? values(selectiveImportDiff).some(item => item.status === "pending") : false
);

/**
 * It is no longer possible to have a full import (with tickets).
 * This selector is now only used to check if the full import was done in the past.
 * We want to only show a full import read-only status for users who have already done a full import,
 * but it is still possible to import other data from LiveChat, like agents, groups, tags, and canned responses,
 * by choosing selective import.
 */
export const selectHasEverDoneFullLiveChatDataImport = createSelector(
  selectLiveChatIntegration,
  integration => integration?.import?.status === "complete"
);

export const selectLiveChatFullImportStatusUpdateTime = createSelector(
  selectLiveChatIntegration,
  liveChatIntegration => liveChatIntegration?.import?.statusChangedAt
);

export const selectLiveChatTicketsImported = createSelector(
  selectLiveChatIntegration,
  liveChatIntegration => liveChatIntegration?.import?.ticketsImported
);

export const selectHasDoneLiveChatSelectiveImportBefore = createSelector(
  selectLiveChatSelectiveImport,
  selectiveImport => (selectiveImport ? values(selectiveImport).some(item => item.status === "complete") : false)
);

export const selectHasDoneLiveChatAgentsImportBefore = createSelector(selectLiveChatSelectiveImport, selectiveImport =>
  selectiveImport ? selectiveImport.agents.status === "complete" : false
);

export const selectHasLiveChatIntegration = createSelector(
  selectLiveChatIntegration,
  integration => integration !== undefined
);

export const selectIsLiveChatIntegrationActive = createSelector(
  selectLiveChatIntegration,
  integration => integration !== undefined && integration.active
);

export const selectShopifyIntegration = createSelector(selectActiveIntegrations, integrations =>
  integrations.find(integration => integration.type === IntegrationType.Shopify)
);

export const selectIsManagedByShopify = createSelector(
  selectShopifyIntegration,
  shopifyIntegration => shopifyIntegration !== undefined
);

export const selectIsShopifyTrial = createSelector(
  selectIsManagedByShopify,
  selectCurrentSubscription,
  (isManagedByShopify, currentSubscription) => {
    return Boolean(
      isManagedByShopify && currentSubscription.trialExpiresAt && isFuture(currentSubscription.trialExpiresAt)
    );
  }
);

export function selectCanImportDataFromLiveChat(state: IWithSessionState & IWithAgentsState): boolean {
  return hasRight(state, Right.ImportDataFromLiveChat);
}

export const selectVisitedLiveChatIntegrationTutorial = createSelector(selectLiveChatIntegration, integration =>
  integration ? integration.flags?.visitedLiveChatIntegrationTutorial === true : false
);

export const selectIntegrationCreatedTicket = createSelector(selectLiveChatIntegration, integration =>
  integration ? integration.flags?.ticketCreated === true : false
);

export function selectLivechatGroupsDataStatus(state: IWithIntegrationsState): DataStatus {
  const livechatIntegration = selectLiveChatIntegration(state);

  if (!livechatIntegration) {
    return DataStatus.NotNeeded;
  }

  const { groups } = livechatIntegration;

  return groups ? (groups.status as DataStatus) : DataStatus.Loading;
}

export function selectHasLivechatAgentsImported(
  state: IWithIntegrationsState & IWithAgentsSessionState & IWithLicensesState
): boolean {
  const hasDoneSelectiveAgentsImportBefore = selectHasDoneLiveChatAgentsImportBefore(state);
  const hasDoneFullImportBefore = selectHasEverDoneFullLiveChatDataImport(state);
  const licenseCreatorId = selectLicenseCreatorId(state);
  const agents = selectAgents(state);

  const hasImportedAgents = Object.values(agents).some(
    agent => agent.createdBy === SYSTEM_USER_ID && agent.id !== licenseCreatorId
  );

  return (hasDoneSelectiveAgentsImportBefore || hasDoneFullImportBefore) && hasImportedAgents;
}

export function selectLivechatGroups(state: IWithIntegrationsState): ILivechatGroup[] {
  const dataStatus = selectLivechatGroupsDataStatus(state);
  const integration = selectLiveChatIntegration(state);

  if (!integration) {
    return [];
  }

  return dataStatus === DataStatus.Ready ? (integration.groups?.data ?? []) : [];
}

export function selectOpenWidget(state: IWithIntegrationsState): IIntegration | null {
  const integrations = selectActiveIntegrations(state);

  return integrations.find(integration => integration.type === IntegrationType.OpenWidget) || null;
}

export function selectOpenWidgetId(state: IWithIntegrationsState): string | null {
  const openWidgetIntegration = selectOpenWidget(state);
  return openWidgetIntegration ? openWidgetIntegration?.id : null;
}

export function selectOpenWidgetExternalId(state: IWithIntegrationsState): string | null {
  const openWidgetIntegration = selectOpenWidget(state);
  return openWidgetIntegration ? openWidgetIntegration?.externalId : null;
}

export function selectIsOpenWidgetConnected(state: IWithIntegrationsState): boolean {
  const openWidgetIntegration = selectOpenWidget(state);
  return !!openWidgetIntegration;
}

export function selectIsOpenWidgetTeamValid(state: IWithIntegrationsState & IWithTeamsState): boolean {
  const openWidgetIntegration = selectOpenWidget(state);
  const teams = selectTeams(state);

  return !!(openWidgetIntegration && teams[openWidgetIntegration.settings?.teamID] !== undefined);
}
